import React from 'react';
import { Settings, Play, Square, RotateCcw } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент для быстрых настроек таймера в боковой панели
 */
const QuickTimerSettings = ({ 
  formData, 
  setFormData, 
  timerThemes,
  startTimer,
  stopTimer,
  resetTimer,
  isRunning
}) => {
  
  // Предопределенные быстрые настройки времени
  const quickTimePresets = [
    { minutes: 1, label: '1 мин' },
    { minutes: 5, label: '5 мин' },
    { minutes: 15, label: '15 мин' },
    { minutes: 30, label: '30 мин' },
    { hours: 1, label: '1 час' },
    { hours: 2, label: '2 часа' }
  ];
  
  // Обработчик выбора предустановленного времени
  const handleTimePresetClick = (preset) => {
    setFormData(prev => ({
      ...prev,
      timerMode: 'timer',
      timerHours: preset.hours || 0,
      timerMinutes: preset.minutes || 0,
      timerSeconds: 0
    }));
  };
  
  // Обработчик переключения режима таймера
  const handleTimerModeToggle = () => {
    setFormData(prev => ({
      ...prev,
      timerMode: prev.timerMode === 'date' ? 'timer' : 'date'
    }));
  };
  
  // Обработчик переключения типа таймера
  const handleTimerTypeToggle = () => {
    setFormData(prev => ({
      ...prev,
      timerType: prev.timerType === 'countdown' ? 'countup' : 'countdown'
    }));
  };
  
  // Обработчик выбора темы
  const handleThemeChange = (themeId) => {
    setFormData(prev => ({
      ...prev,
      theme: themeId
    }));
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Settings className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрые настройки
      </h3>
      
      <div className="space-y-5">
        {/* Кнопки управления */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Управление таймером
          </div>
          <div className="flex gap-2">
            <Button
              primary={!isRunning}
              variant={isRunning ? "secondary" : "primary"}
              size="sm"
              icon={<Play className="w-4 h-4" />}
              onClick={startTimer}
              disabled={isRunning}
              fullWidth
            >
              Запустить
            </Button>
            <Button
              variant="secondary"
              size="sm"
              icon={<Square className="w-4 h-4" />}
              onClick={stopTimer}
              disabled={!isRunning}
              fullWidth
            >
              Стоп
            </Button>
            <Button
              variant="secondary"
              size="sm"
              icon={<RotateCcw className="w-4 h-4" />}
              onClick={resetTimer}
              fullWidth
            >
              Сброс
            </Button>
          </div>
        </div>
        
        {/* Переключатели режимов */}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={handleTimerModeToggle}
            className={`px-3 py-2 text-xs rounded border transition-colors
              ${formData.timerMode === 'date' 
                ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
          >
            До даты
          </button>
          <button
            type="button"
            onClick={handleTimerModeToggle}
            className={`px-3 py-2 text-xs rounded border transition-colors
              ${formData.timerMode === 'timer' 
                ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
          >
            Таймер
          </button>
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={handleTimerTypeToggle}
            className={`px-3 py-2 text-xs rounded border transition-colors
              ${formData.timerType === 'countdown' 
                ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
          >
            Обратный отсчёт
          </button>
          <button
            type="button"
            onClick={handleTimerTypeToggle}
            className={`px-3 py-2 text-xs rounded border transition-colors
              ${formData.timerType === 'countup' 
                ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
          >
            Прямой отсчёт
          </button>
        </div>
                
        {/* Быстрые предустановки времени (для режима таймера) */}
        {formData.timerMode === 'timer' && (
          <>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Быстрые предустановки времени
            </div>
            <div className="grid grid-cols-3 gap-2">
              {quickTimePresets.map((preset, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleTimePresetClick(preset)}
                  className={`px-2 py-1 text-xs rounded border transition-colors
                    ${formData.timerHours === (preset.hours || 0) && formData.timerMinutes === (preset.minutes || 0) && formData.timerSeconds === 0
                      ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </>
        )}
        
        {/* Быстрый выбор темы */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Быстрая смена темы
          </div>
          <div className="grid grid-cols-4 gap-2">
            {timerThemes.slice(0, 4).map(theme => (
              <div 
                key={theme.id}
                onClick={() => handleThemeChange(theme.id)}
                className={`cursor-pointer p-2 rounded-lg border transition-all ${
                  formData.theme === theme.id 
                    ? 'border-indigo-500 ring-1 ring-indigo-200 dark:ring-indigo-800' 
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <div 
                  className="w-full h-4 rounded" 
                  style={{
                    background: theme.id === 'russian-flag' 
                      ? 'linear-gradient(to bottom, white 33%, blue 33%, blue 66%, red 66%)' 
                      : theme.id === 'victory-day'
                      ? 'linear-gradient(to right, #ff7043 50%, #000000 50%)'
                      : `linear-gradient(to right, ${theme.primaryColor}, ${theme.secondaryColor})`
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg mt-6">
        <div className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">
          Совет дня
        </div>
        <p className="text-xs text-indigo-700 dark:text-indigo-300 mt-1">
          Для важных событий рекомендуется настроить не только таймер, но и браузерное уведомление, чтобы не пропустить момент.
        </p>
      </div>
    </div>
  );
};

export default QuickTimerSettings;